import React, { FC, useState } from 'react';
import { Button, Drawer } from 'antd';
import {
  CardCollapse,
  Descriptions,
  DescriptionsRow,
  Divider,
  IconBackground,
  Notification,
  UserRoleChip,
  WarningCard,
} from 'common/components';
import { EColor } from 'common/const';
import { dateFormatter, getEmail, phoneFormatter, priceFormatter, showMessage } from 'common/helpers';
import { AuthHooks } from 'common/hooks';
import { useUserContext } from 'common/context';
import { ISubdivisionShort } from 'common/models';
import { Affiliate, Bin, Contract, Copy, Edit, Plus } from 'common/svg';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { RemoveUserSubdivisionModal, UserCardRoleSelector } from 'entities/User/components';
import { EUserRoleLabel, EUserStatus } from 'entities/User/User.enum';
import { calculateUserBalance, getUserName, getUserRole, getUserRoleChipColor } from 'entities/User/User.helper';
import { SubdivisionTreeModal } from 'entities/Subdivision/components';
import {
  addUserSubdivision,
  deleteUser,
  removeUserSubdivision,
  selectCurrentUser,
  selectIsUserLoading,
  selectUser,
  selectUserContracts,
  updateUserByAdmin,
} from 'entities/User/User.slice';
import { selectSubdivisionList } from 'entities/Subdivision/Subdivision.slice';

interface IComponentProps {
  subdivisionId?: number;
}

export const UserCard: FC<IComponentProps> = ({ subdivisionId }) => {
  const dispatch = useAppDispatch();
  // subdivision
  const subdivisionList = useAppSelector(selectSubdivisionList);
  // user
  const currentUser = useAppSelector(selectCurrentUser);
  const user = useAppSelector(selectUser);
  const isUserLoading = useAppSelector(selectIsUserLoading);
  const userContracts = useAppSelector(selectUserContracts);

  const [openWarningCard, setOpenWarningCard] = useState<boolean>(false);
  const [openSubdivisionTreeModal, setOpenSubdivisionTreeModal] = useState<boolean>(false);
  const [openRemoveUserSubdivisionModal, setOpenRemoveUserSubdivisionModal] = useState<boolean>(false);
  const [subdivisionToRemove, setSubdivisionToRemove] = useState<ISubdivisionShort | null>(null);
  const { openUserCard, setOpenUserCard, setOpenEditUserCard } = useUserContext();
  const { isSeller } = AuthHooks.useIsSeller();

  const isActivatedUser = user?.status === EUserStatus.Active;
  const { isUser } = getUserRole(user?.roles);
  const balance = calculateUserBalance(user);

  const handleCancel = (): void => {
    if (isUserLoading) {
      return;
    }

    setOpenWarningCard(false);
    setOpenUserCard(false);
  };

  const handleOpenSubdivisionTreeModal = (e: React.MouseEvent): void => {
    e.stopPropagation();
    setOpenSubdivisionTreeModal(true);
  };

  const closeSubdivisionTreeModal = (): void => {
    setOpenSubdivisionTreeModal(false);
  };

  const handleAddUserSubdivision = (subdivisionId: number, onSuccess: () => void): void => {
    if (!user) {
      return;
    }

    dispatch(
      addUserSubdivision({
        userId: user.id,
        subdivisionId,
        onSuccess,
      }),
    );
  };

  const handleOpenRemoveUserSubdivisionModal = (subdivision: ISubdivisionShort): void => {
    setSubdivisionToRemove(subdivision);
    setOpenRemoveUserSubdivisionModal(true);
  };

  const closeRemoveUserSubdivisionModal = (): void => {
    setSubdivisionToRemove(null);
    setOpenRemoveUserSubdivisionModal(false);
  };

  const handleRemoveUserSubdivision = (): void => {
    if (!user || !subdivisionToRemove) {
      return;
    }

    dispatch(
      removeUserSubdivision({
        userId: user.id,
        subdivisionId: subdivisionToRemove.id,
        onSuccess: () => {
          showMessage({ content: 'Пользователь удалён из подразделения.', icon: <Bin className="icon-dark-grey" /> });
          setSubdivisionToRemove(null);
          setOpenRemoveUserSubdivisionModal(false);
        },
      }),
    );
  };

  const handleDeleteUser = (): void => {
    if (user) {
      dispatch(
        deleteUser({
          id: user.id,
          onSuccess: () => {
            showMessage({
              icon: <Bin className="icon-bin-dark-grey" />,
              content: `Пользователь «${getEmail(user.email)}» удален.`,
            });
            handleCancel();
          },
        }),
      );
    }
  };

  const unlinkUserSubdivision = (): void => {
    if (!user || !subdivisionId) {
      return;
    }

    dispatch(
      removeUserSubdivision({
        userId: user.id,
        subdivisionId,
        onSuccess: handleCancel,
      }),
    );
  };

  const copyEmail = (e: any) => {
    e.stopPropagation();

    if (user?.email) {
      navigator.clipboard.writeText(user.email);
    }
  };

  const copyPhone = (e: any) => {
    e.stopPropagation();

    if (user?.phone) {
      navigator.clipboard.writeText(user.phone);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <>
      <Drawer
        rootClassName="redesign drawer user-card"
        destroyOnClose
        width={720}
        open={openUserCard}
        onClose={handleCancel}
        classNames={{ footer: 'justify-between' }}
        footer={[
          subdivisionId ? (
            <Button key="delete" className="button-lg deleting" loading={isUserLoading} onClick={unlinkUserSubdivision}>
              Отвязать
            </Button>
          ) : (
            <Button
              key="delete"
              className="button-lg deleting"
              icon={<Bin className="icon-bin-pale-red" />}
              disabled={user.id === currentUser?.id || user.isAccountOwner}
              onClick={() => {
                setOpenUserCard(false);
                setOpenWarningCard(true);
              }}
            >
              Удалить
            </Button>
          ),
          <Button
            key="edit"
            className="button-lg secondary"
            icon={<Edit className="icon-edit-dark-grey" />}
            loading={isUserLoading}
            onClick={() => {
              setOpenEditUserCard(true);
            }}
          >
            Редактировать
          </Button>,
        ]}
      >
        <div className="flex-1">
          <div className="mb-14 ff-regular fs-xxl">{getUserName.fullName(user.lastName, user.firstName, user.patronymic)}</div>

          {user.limit && balance && (
            <div className="mb-9 flex align-center gap-11">
              <div>
                <div className="mb-2 ff-medium fs-s">{priceFormatter(user.limit)}</div>
                <span className="ff-regular fs-xs color-dark-grey">Персональный лимит</span>
              </div>

              <div>
                <div className="mb-2 ff-medium fs-s">{priceFormatter(balance)}</div>
                <span className="ff-regular fs-xs color-dark-grey">Остаток за период</span>
              </div>
            </div>
          )}

          <div className="mb-11 flex align-center wrap" style={{ gap: 12 }}>
            {isUser ? (
              <UserRoleChip color={EColor.LightGrey} name={EUserRoleLabel.user} />
            ) : (
              user.roles.map((role) => {
                const color = getUserRoleChipColor(role);
                return <UserRoleChip key={role} color={color} name={EUserRoleLabel[role]} />;
              })
            )}

            <UserCardRoleSelector
              roles={user.roles}
              isAccountOwner={user.isAccountOwner}
              isSeller={isSeller}
              // @ts-ignore
              onChange={(value) => dispatch(updateUserByAdmin({ id: user.id, roles: value }))}
            />
          </div>

          {!isSeller && !isActivatedUser && (
            <div className="mb-11">
              <Notification type="error" description="Пользователь не активирован" />
            </div>
          )}

          {!isSeller && isActivatedUser && !user.subdivisions?.length && (
            <div className="mb-11">
              <Notification type="warning" description="Подразделение не назначено" />
            </div>
          )}

          <div className="mb-14">
            <CardCollapse
              keys={['info']}
              items={[
                {
                  key: 'info',
                  label: <span className="ff-medium fs-m">Общая информация</span>,
                  children: (
                    <Descriptions>
                      {user.position && <DescriptionsRow label="Должность" value={user.position} />}
                      <DescriptionsRow
                        label="Электронный адрес"
                        value={
                          <div className="flex align-center justify-between">
                            <span className="flex-1 ff-regular fs-s word-break-all">{getEmail(user.email)}</span>
                            {user.email && <Copy className="icon-dark-grey cursor-pointer" onClick={copyEmail} />}
                          </div>
                        }
                      />
                      {user.phone && (
                        <DescriptionsRow
                          label="Контактный телефон"
                          value={
                            <div className="flex align-center justify-between">
                              <span className="flex-1 ff-regular fs-s word-break-all">{phoneFormatter(user.phone)}</span>
                              <Copy className="icon-dark-grey cursor-pointer" onClick={copyPhone} />
                            </div>
                          }
                        />
                      )}
                      {user.activatedAt ? (
                        <DescriptionsRow label="Активирован" value={dateFormatter('DD.MM.YYYY', user.activatedAt)} />
                      ) : (
                        <DescriptionsRow label="Приглашение отправлено" value={dateFormatter('DD.MM.YYYY', user.createdAt)} />
                      )}
                    </Descriptions>
                  ),
                },
              ]}
            />

            {/* TODO For future improvement */}
            {/* {!!user.workspaces?.length && (
              <>
                <Divider marginY={20} />

                <CardCollapse
                  keys={['workspaces']}
                  items={[
                    {
                      key: 'workspaces',
                      label: <span className="ff-medium fs-m">Карта оснащения</span>,
                      children: user.workspaces.map((workspace) => {
                        return (
                          <div key={workspace.id} className="mb-5 flex align-center">
                            <div className="mr-4">
                              <IconBackground
                                content={<Document className="icon-document-dark-grey" />}
                                color={EColor.LightGrey}
                              />
                            </div>

                            <span className="ff-regular fs-s ellipsis-2" title={workspace.name}>
                              {workspace.name}
                            </span>
                          </div>
                        );
                      }),
                    },
                  ]}
                />
              </>
            )} */}

            {!isSeller && (
              <>
                <Divider marginY={20} />

                <CardCollapse
                  keys={['subdivisions']}
                  items={[
                    {
                      key: 'subdivisions',
                      label: (
                        <div className="flex align-center justify-between">
                          <span className="ff-medium fs-m">Подразделения пользователя</span>

                          <Button
                            className="button-text dark-green"
                            icon={<Plus className="icon-dark-green" />}
                            onClick={handleOpenSubdivisionTreeModal}
                          >
                            Добавить
                          </Button>
                        </div>
                      ),
                      children: user.subdivisions?.length ? (
                        user.subdivisions.map((item) => {
                          return (
                            <div key={item.id} className="mb-5 flex align-start justify-between">
                              <div className="flex-1 flex align-start">
                                <div className="mr-4">
                                  <IconBackground
                                    content={<Affiliate className="icon-affiliate-dark-grey" />}
                                    color={EColor.LightGrey}
                                  />
                                </div>

                                <span className="mt-2 ff-regular fs-s" title={item.name}>
                                  {item.name}
                                </span>
                              </div>

                              <div className="mt-2">
                                <Button
                                  className="button-icon"
                                  icon={<Bin className="icon-pale-red" />}
                                  onClick={() => handleOpenRemoveUserSubdivisionModal(item)}
                                />
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <span className="ff-regular fs-s color-dark-grey">Нет привязанных подразделений</span>
                      ),
                    },
                  ]}
                />
              </>
            )}

            {isSeller && !!userContracts?.length && (
              <>
                <Divider marginY={20} />

                <CardCollapse
                  keys={['contracts']}
                  items={[
                    {
                      key: 'contracts',
                      label: <span className="ff-medium fs-m">Контракты</span>,
                      children: userContracts.map((contract) => {
                        return (
                          <div key={contract.id} className="mb-5 flex align-center">
                            <div className="mr-4">
                              <IconBackground
                                content={<Contract className="icon-contract-dark-grey" />}
                                color={EColor.LightGrey}
                              />
                            </div>

                            <span className="ff-regular fs-s ellipsis-2" title={contract.buyerName}>
                              {contract.buyerName}
                            </span>
                          </div>
                        );
                      }),
                    },
                  ]}
                />
              </>
            )}
          </div>
        </div>
      </Drawer>

      <WarningCard
        open={openWarningCard}
        content="Вы уверены, что хотите удалить пользователя?"
        subtitle="Отменить данное действие будет невозможно."
        confirmBtnTitle="Удалить"
        loading={isUserLoading}
        onClose={() => {
          setOpenWarningCard(false);
          setOpenUserCard(true);
        }}
        onConfirm={handleDeleteUser}
      />

      <SubdivisionTreeModal
        open={openSubdivisionTreeModal}
        loading={isUserLoading}
        placeholder="Привязать к..."
        account={user.account.name}
        subdivisions={subdivisionList}
        onCancel={closeSubdivisionTreeModal}
        onSubmit={handleAddUserSubdivision}
      />

      <RemoveUserSubdivisionModal
        open={openRemoveUserSubdivisionModal}
        loading={isUserLoading}
        onCancel={closeRemoveUserSubdivisionModal}
        onSubmit={handleRemoveUserSubdivision}
      />
    </>
  );
};
